/**
 * The two directions between a guide's stored HTML and the editor's document.
 *
 * A document model is not a text editor: it keeps a tree, and serialising that
 * tree gives valid markup in the MODEL's shape rather than in the shape the
 * handbook is written in. Three differences are real, all three are invisible
 * to a reader, and all three are undone here - because left alone they would
 * rewrite twenty-two articles the first time somebody opened one, and a diff
 * that says "everything changed" is a diff nobody reads.
 *
 *   1. A list item's content is a block, so `<li>text</li>` comes back as
 *      `<li><p>text</p></li>`, and the same for a table cell. Harmless markup,
 *      visible result: the reader gives a paragraph a bottom margin, so every
 *      checklist and every table row would gain a gap.
 *   2. Tables are one `<tbody>` with header cells marked `<th>`; the handbook
 *      writes `<thead>` then `<tbody>`.
 *   3. Every cell is serialised with `colspan="1" rowspan="1"`, which is what
 *      "no span" already means.
 *   4. A `>` inside an attribute comes back unescaped. Legal HTML, and the one
 *      difference here that is not only cosmetic - see `escapeAttrGt`.
 *   5. A live editor keeps an empty paragraph at the end so there is somewhere
 *      to put the caret after the last block. It belongs to the editor, not to
 *      the article.
 */
import { generateHTML, generateJSON } from "@tiptap/html";
import type { Extensions, JSONContent } from "@tiptap/core";
import { formatKbHtml } from "./format";

/** Stored HTML into the editor's document. */
export function kbHtmlToDoc(html: string, extensions: Extensions): JSONContent {
  return generateJSON(html, extensions);
}

/** The editor's document back into the HTML a guide is stored as. */
export function kbDocToHtml(doc: JSONContent, extensions: Extensions): string {
  return formatKbHtml(normaliseKbHtml(generateHTML(doc, extensions)));
}

/** The model-shaped differences, undone. Exported for its own test. */
export function normaliseKbHtml(html: string): string {
  return dropTrailingEmptyParagraphs(escapeAttrGt(splitTableHead(dropUnitSpans(unwrapCellParagraphs(html)))));
}

/**
 * The empty paragraph a live editor keeps at the end, removed.
 *
 * ProseMirror needs somewhere to put the caret after a table or a picture slot,
 * so it holds a paragraph there. Saved, that paragraph is a blank line at the
 * bottom of every article that happens to end in a block - and the next person
 * to open it gets another one.
 */
export function dropTrailingEmptyParagraphs(html: string): string {
  return html.replace(/(?:<p><\/p>)+$/, "");
}

/**
 * `<li><p>x</p></li>` back to `<li>x</li>`, and the same for `<td>` and `<th>`,
 * but only when the paragraph is the cell's whole content.
 *
 * A cell that genuinely holds two blocks keeps its paragraph, because there the
 * wrapper is the markup rather than an artefact - dropping it would put a bare
 * text node beside a list, which is neither valid nor what was written.
 */
export function unwrapCellParagraphs(html: string): string {
  return html.replace(
    /<(li|td|th)\b((?:"[^"]*"|[^>])*)>\s*<p>([\s\S]*?)<\/p>\s*<\/\1>/g,
    (whole, tag: string, attrs: string, inner: string) =>
      /<(p|ul|ol|div|table|h[234]|figure|blockquote|pre)\b/.test(inner)
        ? whole
        : `<${tag}${attrs}>${inner}</${tag}>`,
  );
}

/** `colspan="1"` and `rowspan="1"` say nothing, so they are not written down. */
export function dropUnitSpans(html: string): string {
  return html.replace(/\s(?:colspan|rowspan)="1"/g, "");
}

/**
 * A table's leading all-`<th>` row moved into a `<thead>`.
 *
 * The document model has no thead: a header cell is a cell that knows it is
 * one. Writing the grouping back is what keeps a seeded guide and its database
 * copy the same text, which is the difference between "this article was edited"
 * and "this article was opened".
 */
export function splitTableHead(html: string): string {
  return html.replace(/<tbody>([\s\S]*?)<\/tbody>/g, (whole, body: string) => {
    const first = body.match(/^\s*<tr>[\s\S]*?<\/tr>/);
    if (!first) return whole;
    const head = first[0];
    // Every cell in the row is a header cell, and there is at least one.
    if (/<td\b/.test(head) || !/<th\b/.test(head)) return whole;
    return `<thead>${head.trim()}</thead><tbody>${body.slice(head.length)}</tbody>`;
  });
}

const TAG_WITH_ATTRS = /<([a-zA-Z][a-zA-Z0-9]*)((?:"[^"]*"|[^>])*)>/g;

/**
 * `>` inside an attribute value written back as `&gt;`.
 *
 * A bare `>` in a quoted attribute is valid HTML and every browser reads it
 * correctly, so this looks like tidiness. It is not. A picture slot's caption is
 * routinely a path - "CRM &gt; Hoidla" - and two things downstream read the body
 * with a plain `/<[^>]*>/` sweep that stops at the FIRST `>` it sees. Hand them
 * an unescaped one and the tag appears to end in the middle of the caption: the
 * rest of the sentence leaks into the article's plain text, which is what search
 * indexes and what the reading time is counted from.
 *
 * The seeds already escape it. This keeps that true for anything the editor
 * writes, so a caption typed with an arrow stays a caption.
 */
export function escapeAttrGt(html: string): string {
  return html.replace(
    TAG_WITH_ATTRS,
    (_whole, tag: string, attrs: string) =>
      `<${tag}${attrs.replace(/"([^"]*)"/g, (_a, value: string) => `"${value.replace(/>/g, "&gt;")}"`)}>`,
  );
}
